import store from '../store/store.js'
import env from '../env';
import { constants } from '../utils/constants';

const noNavRoutes = ['Home', 'FillForm', 'ViewForm', 'WaitForPDF', 'Office', 'FormBuilder', 'SignatureTerms', 'Login', 'ResetPassword', 'NotFound', 'PaymentRedirect', 'MaintenanceMode', 'Embed'];
const noAuthRoutes = ['FillForm', 'Login', 'ResetPassword', 'NotFound', 'PaymentRedirect', 'MaintenanceMode', 'Embed'];
const mobileOptimizedRoutes = ['FillForm', 'ViewForm'];

async function waitForEnv(to, from, next) {
    while (!env.API_ENDPOINT) {
        await new Promise(res => setTimeout(res, 50));
    }
    next();
}

async function checkAppStatus(to, from, next) {
    if (to.name === 'MaintenanceMode') {
        next();
        return;
    }

    await store.dispatch('base/updateAppStatus');
    let status = store.getters['base/appStatus'];
    // if app is expired, reload when navigating to a convenient page
    if (store.getters['base/appLoadedAt'] < status.APP_EXPIRE && ['Boxes', 'FormBuilder', 'FormLibrary', 'Office'].includes(to.name)) {
        location.href = store.getters['base/url'] + to.path;
    }

    // check for maintenance mode
    let isFiller = to.name === 'FillForm';
    if (status.MAINTENANCE_MODE || (isFiller && status.FILLER_MAINTENANCE_MODE)) {
        store.commit('base/setFromRoute', to);
        next({ replace: true, path: '/maintenance' });
    } else {
        next();
    }
}

async function clearNotifications(to, from, next) {
    if (to.name !== from.name) {
        store.dispatch('notification/clear');
        store.dispatch('dialog/clear');
    }
    next();
}

function updateNavBar(to, from, next) {
    if (to.name !== 'MaintenanceMode' && (!to.path.startsWith('/settings') || !from.path.startsWith('/settings'))) {
        store.commit('base/setFromRoute', { name: from.name, path: from.path });
    }
    store.commit('layout/showNav', !noNavRoutes.includes(to.name));
    next();
}

// load user if not existing and paperwork JWT present
async function loadUser(to, from, next) {
    if (to.path.substr(1, 5) === 'login' && store.getters['auth/authToken']) {
        next('/');
        return;
    }
    let isAuthRoute = !noAuthRoutes.includes(to.name);
    store.commit('base/isAuthRoute', isAuthRoute);
    
    if (isAuthRoute && !store.getters['user/exists'] && store.getters['auth/authToken']) {
        let promises = [store.dispatch('user/load'), store.dispatch('org/loadInfo', ['users', 'boxes', 'org'])];
        if (store.getters['auth/decodedAuthToken'].region && !store.getters['dataElements/getDefinitions'].length) {
            promises.push(store.dispatch('dataElements/loadDataElementDefinitions'));
        }
        await Promise.all(promises);
    }
    next();
}

async function loadPartnerBranding(to, from, next) {
    // Use the current domain name to load partner branding settings if we're on a
    // route that doesn't load an organization
    if (
        ['Login', 'ResetPassword', 'NotFound', 'FormLibrary'].includes(to.name) &&
        !store.getters['org/exists']
    ) {
        await store.dispatch('base/loadPartnerBranding');
    }
    next();
}

async function updateMetadata(to, from, next) {
    // Find the nearest route with a title set
    const appName = store.getters['base/appName'];
    let nearest = to.matched.slice().reverse().find(r => r.meta && r.meta.title);
    if (nearest) {
        document.title = nearest.meta.title + ' - ' + appName;
    } else if (!to.meta.customTitle) {
        document.title = appName;
    }

    // Remove any old router-controlled meta tags from the document
    Array.from(document.querySelectorAll('[data-vue-router-controlled]')).map(el => el.parentNode.removeChild(el));

    // Find the nearest route with meta tags set
    nearest = to.matched.slice().reverse().find(r => r.meta && r.meta.tags);
    if (nearest) {
        // Add the meta tag definitions into the head
        for (let tag of nearest.meta.tags) {
            const elem = document.createElement('meta');
            elem.setAttribute('data-vue-router-controlled', '');
            for (let key in tag) {
                elem.setAttribute(key, tag[key]);
            }
            document.head.appendChild(elem);
        }
    }

    // Set viewport meta tag to zoomed-out on mobile for non-mobile-optimized routes
    if (constants.USER_AGENT.isMobile && !mobileOptimizedRoutes.includes(to.name)) {
        document.querySelector('meta[name="viewport"]')
            .setAttribute('content', 'width=1280,initial-scale=0.3');
    }

    next();
}

async function checkRole(to, from, next) {
    let minRole = to.meta && to.meta.minRole ? to.meta.minRole : '';
    let user = store.getters['user/exists'] ? store.getters['user/user'] : false;

    if (minRole && !user) { // login required
        await store.dispatch('auth/login', to);
        return;
    } else if (minRole && user && !user.hasRole(minRole)) { // deny access
        await store.dispatch('notification/set', {
            type: 'error',
            message: 'You do not have permission to access the requested page',
            timeout: 2000
        });
        next('/');
    } else {
        next();
    }
}

function updateCurrentRouteInStore(to) {
    store.commit('base/currentRoute', to);
}

export default {
    checkAppStatus,
    loadPartnerBranding,
    checkRole,
    clearNotifications,
    loadUser,
    updateMetadata,
    updateNavBar,
    waitForEnv,
    updateCurrentRouteInStore,
};